/**
 * Database Availability Service
 * 
 * Service để kiểm tra và cache database availability từ server,
 * tự động fallback sang database hệ thống nếu không có từ server.
 * Cache kết quả để tránh check lại nhiều lần và tránh treo khi load web.
 */

import { API_CONFIG } from '../../config/api.config'

interface DatabaseAvailability {
  teacherId: string
  lop: string
  chuong: string
  bai: string
  hasServerDatabase: boolean
  hasIndexedDB: boolean
  lastChecked: number
  expiresAt: number
}

interface DatabaseAvailabilityCache {
  [key: string]: DatabaseAvailability
}

class DatabaseAvailabilityService {
  private readonly CACHE_KEY = 'database_availability_cache'
  private readonly CACHE_DURATION = 5 * 60 * 1000 // 5 phút
  private readonly SERVER_TIMEOUT = 3000 // 3 giây
  private readonly INDEXEDDB_TIMEOUT = 2000 // 2 giây

  private cache: DatabaseAvailabilityCache = {}

  constructor() {
    this.loadCache()
  }

  /**
   * Load cache từ localStorage
   */
  private loadCache(): void {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY)
      if (cached) {
        this.cache = JSON.parse(cached)
        // Clean expired entries
        this.cleanExpiredEntries()
      }
    } catch (error) {
      console.warn('⚠️ [DatabaseAvailability] Error loading cache:', error)
      this.cache = {}
    }
  }

  /**
   * Save cache vào localStorage
   */
  private saveCache(): void {
    try {
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(this.cache))
    } catch (error) {
      console.warn('⚠️ [DatabaseAvailability] Error saving cache:', error)
    }
  }

  /**
   * Clean expired entries
   */
  private cleanExpiredEntries(): void {
    const now = Date.now()
    const keys = Object.keys(this.cache)
    let cleaned = false

    for (const key of keys) {
      if (this.cache[key].expiresAt < now) {
        delete this.cache[key]
        cleaned = true
      }
    }

    if (cleaned) {
      this.saveCache()
    }
  }

  /**
   * Generate cache key
   */
  private getCacheKey(teacherId: string, lop: string, chuong: string, bai: string): string {
    return `${teacherId}_L${lop}_C${chuong}_B${bai}`
  }

  /**
   * Check if database is available from server (with timeout)
   */
  private async checkServerDatabase(
    teacherId: string,
    lop: string,
    chuong: string,
    bai: string
  ): Promise<boolean> {
    try {
      // Extract numbers
      const lopNum = lop.match(/(\d+)/)?.[1] || lop
      const chuongNum = chuong.match(/(\d+)/)?.[1] || chuong
      const baiNum = bai.match(/(\d+)/)?.[1] || bai

      // ✅ Use Python Backend Static File Check (avoiding WordPress API)
      const API_HOST = import.meta.env.VITE_API_URL || 'http://localhost:8000'
      // Check TN.json presence as indicator for Quiz database
      const apiUrl = `${API_HOST}/teacher_content/${teacherId}/L${lopNum}/C${chuongNum}/B${baiNum}/TN.json?t=${Date.now()}`

      // Check với timeout
      const response = await Promise.race([
        fetch(apiUrl, {
          method: 'GET',
          cache: 'no-cache',
          headers: { 'Accept': 'application/json' }
        }),
        new Promise<Response>((_, reject) =>
          setTimeout(() => reject(new Error('Server timeout')), this.SERVER_TIMEOUT)
        )
      ])

      return response.ok
    } catch (error) {
      console.warn(`⚠️ [DatabaseAvailability] Server check failed for ${teacherId}/L${lop}/C${chuong}/B${bai}:`, error)
      return false
    }
  }

  /**
   * Check if database is available from IndexedDB (with timeout)
   */
  private async checkIndexedDB(
    teacherId: string,
    lop: string,
    chuong: string,
    bai: string
  ): Promise<boolean> {
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        resolve(false)
      }, this.INDEXEDDB_TIMEOUT)

      const request = indexedDB.open('TeacherContentDB', 6)

      request.onerror = () => {
        clearTimeout(timeout)
        resolve(false)
      }

      request.onsuccess = () => {
        try {
          const db = request.result
          if (!db.objectStoreNames.contains('databases')) {
            clearTimeout(timeout)
            resolve(false)
            return
          }

          const transaction = db.transaction(['databases'], 'readonly')
          const store = transaction.objectStore('databases')
          const id = `${teacherId}_L${lop}_C${chuong}_B${bai}_TN`
          const getRequest = store.get(id)

          getRequest.onsuccess = () => {
            clearTimeout(timeout)
            resolve(!!getRequest.result?.data)
          }

          getRequest.onerror = () => {
            clearTimeout(timeout)
            resolve(false)
          }
        } catch (error) {
          clearTimeout(timeout)
          resolve(false)
        }
      }

      request.onupgradeneeded = () => {
        clearTimeout(timeout)
        resolve(false)
      }
    })
  }

  /**
   * Check database availability (server + IndexedDB)
   * Returns cached result if available, otherwise checks and caches
   */
  async checkDatabaseAvailability(
    teacherId: string,
    lop: string,
    chuong: string,
    bai: string
  ): Promise<{ hasServerDatabase: boolean; hasIndexedDB: boolean }> {
    const cacheKey = this.getCacheKey(teacherId, lop, chuong, bai)
    const cached = this.cache[cacheKey]

    // Return cached result if still valid
    if (cached && cached.expiresAt > Date.now()) {
      console.log(`✅ [DatabaseAvailability] Using cached result for ${cacheKey}`)
      return {
        hasServerDatabase: cached.hasServerDatabase,
        hasIndexedDB: cached.hasIndexedDB
      }
    }

    console.log(`🔍 [DatabaseAvailability] Checking database availability for ${cacheKey}...`)

    // Check both in parallel
    const [hasServerDatabase, hasIndexedDB] = await Promise.all([
      this.checkServerDatabase(teacherId, lop, chuong, bai),
      this.checkIndexedDB(teacherId, lop, chuong, bai)
    ])

    // Cache result
    const availability: DatabaseAvailability = {
      teacherId,
      lop,
      chuong,
      bai,
      hasServerDatabase,
      hasIndexedDB,
      lastChecked: Date.now(),
      expiresAt: Date.now() + this.CACHE_DURATION
    }

    this.cache[cacheKey] = availability
    this.saveCache()

    console.log(`✅ [DatabaseAvailability] Cached result for ${cacheKey}:`, {
      hasServerDatabase,
      hasIndexedDB
    })

    return { hasServerDatabase, hasIndexedDB }
  }

  /**
   * Get cached availability without checking
   */
  getCachedAvailability(
    teacherId: string,
    lop: string,
    chuong: string,
    bai: string
  ): { hasServerDatabase: boolean; hasIndexedDB: boolean } | null {
    const cacheKey = this.getCacheKey(teacherId, lop, chuong, bai)
    const cached = this.cache[cacheKey]

    if (cached && cached.expiresAt > Date.now()) {
      return {
        hasServerDatabase: cached.hasServerDatabase,
        hasIndexedDB: cached.hasIndexedDB
      }
    }

    return null
  }

  /**
   * Clear cache for specific lesson
   */
  clearCache(teacherId: string, lop: string, chuong: string, bai: string): void {
    const cacheKey = this.getCacheKey(teacherId, lop, chuong, bai)
    delete this.cache[cacheKey]
    this.saveCache()
  }

  /**
   * Clear all cache
   */
  clearAllCache(): void {
    this.cache = {}
    this.saveCache()
  }
}

export const databaseAvailabilityService = new DatabaseAvailabilityService()
export default databaseAvailabilityService

